/**
 * CreativePerformanceSection.tsx
 *
 * 7-section Creative Performance Analysis dashboard.
 * All data pre-computed by creativePerformanceData.ts — pure UI here.
 *
 * Sections:
 *   1. Creative Funnel Aggregate  — KPI strip
 *   2. Creative Funnel Progression — horizontal bar funnel (aggregate)
 *   3. Hook Strength Comparison   — horizontal bar chart, toggle CTR / Clicks
 *   4. Funnel Efficiency          — 2×2 grid of cost-metric bar charts
 *   5. Funnel Depth vs Outcomes   — bubble / scatter chart with axis selectors
 *   6. Drop-off Analysis          — grouped horizontal bars per transition
 *   7. Creative Ranking           — scored table
 */

import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
  TooltipProps,
} from "recharts";
import { branding } from "../../../../config/branding";
import type { CreativePerformanceRow } from "../creativePerformanceData";
import type { MetaLeadGenRow, MetaLeadGenFilters } from "../types";
import { getTopNAdsData, type IndividualCreativeFunnelRow } from "../individualCreativeBreakdown";

// ─────────────────────────────────────────────
// SHARED CHART PROPS
// ─────────────────────────────────────────────
const TICK  = { fontSize: 10, fill: "var(--muted-foreground)", fontFamily: "inherit" };
const AXIS  = { stroke: "var(--border)" };
const GRID  = { strokeDasharray: "3 3", stroke: "var(--border)", strokeOpacity: 0.4 };
const CHART_H  = 220;
const HCHART_H = 180;

// ─────────────────────────────────────────────
// COST METRIC COLORS (chart palette slots)
// ─────────────────────────────────────────────
const C = branding.colors;
const METRIC_COLORS = [C.chart[0], C.chart[1], C.chart[2], C.chart[3]];

// ─────────────────────────────────────────────
// INDIVIDUAL CREATIVE CARD METRIC CELL
// ─────────────────────────────────────────────
function MetricCell({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[8px] text-muted-foreground uppercase tracking-widest font-semibold">{label}</span>
      <span className={`text-[12px] font-bold leading-tight ${highlight ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
        {value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────
// INDIVIDUAL CREATIVE CARD
// ─────────────────────────────────────────────
function IndividualCreativeCard({ row }: { row: IndividualCreativeFunnelRow }) {
  const hasVideo = row.videoViews > 0;
  const spend = row.spend >= 1000
    ? `$${(row.spend / 1000).toFixed(1)}k`
    : `$${row.spend.toFixed(0)}`;
  const impr = row.impressions >= 1_000_000
    ? `${(row.impressions / 1_000_000).toFixed(1)}M`
    : row.impressions >= 1000
    ? `${(row.impressions / 1000).toFixed(1)}k`
    : String(row.impressions);
  const clicks = row.clicks >= 1000
    ? `${(row.clicks / 1000).toFixed(1)}k`
    : String(row.clicks);

  return (
    <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 hover:border-[var(--brand-primary)]/40 transition-colors">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div
          className="w-2.5 h-2.5 rounded-full mt-0.5 flex-shrink-0"
          style={{ background: row.color }}
        />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-foreground leading-tight line-clamp-2">
            {row.shortName}
          </p>
          <span
            className="text-[8px] font-bold uppercase tracking-widest mt-0.5 inline-block"
            style={{ color: row.color }}
          >
            {row.typeLabel}
          </span>
        </div>
      </div>

      {/* Metric grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        <MetricCell label="Spend"       value={spend} />
        <MetricCell label="Impressions" value={impr} />
        <MetricCell label="CTR"         value={row.impressions > 0 ? `${row.ctr.toFixed(2)}%` : "—"} highlight />
        <MetricCell label="Opt-ins"     value={String(row.optins)} highlight />
        <MetricCell label="Clicks"      value={clicks} />
        <MetricCell label="CPL"         value={row.optins > 0 ? `$${row.costPerOptin.toFixed(2)}` : "—"} />
        {hasVideo && (
          <div className="col-span-2 pt-1 border-t border-border/50">
            <MetricCell
              label="View Rate (3s)"
              value={`${row.videoViewRate.toFixed(1)}%`}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SECTION 0 — Individual Creative Cards
// ─────────────────────────────────────────────
function Section0IndividualCards({ rows, filters }: { rows: MetaLeadGenRow[]; filters: MetaLeadGenFilters }) {
  const adsData = useMemo(() => getTopNAdsData(rows, filters, 20), [rows, filters]);
  if (adsData.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
        {adsData.map(row => (
          <IndividualCreativeCard key={row.adId} row={row} />
        ))}
      </div>

      {/* Video Watch Rate chart */}
      {adsData.some(r => r.videoViews > 0) && (
        <VideoWatchRateChart data={adsData} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// VIDEO WATCH RATE CHART
// ─────────────────────────────────────────────
function VideoWatchRateChart({ data }: { data: IndividualCreativeFunnelRow[] }) {
  const videoAds = useMemo(() =>
    [...data]
      .filter(r => r.videoViews > 0)
      .sort((a, b) => b.videoViewRate - a.videoViewRate),
    [data]
  );
  if (videoAds.length === 0) return null;

  const chartData = videoAds.map(r => ({
    name:       r.shortName.length > 28 ? r.shortName.slice(0, 26) + "…" : r.shortName,
    value:      parseFloat(r.videoViewRate.toFixed(2)),
    views:      r.videoViews,
    color:      r.color,
  }));

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4">
        <p className="text-sm font-bold text-foreground">Video View Rate</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          3-second video views ÷ impressions — measures how well each creative captures attention in feed
        </p>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(120, videoAds.length * 40)}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 0, right: 70, left: 8, bottom: 0 }}
        >
          <CartesianGrid {...GRID} horizontal={false} />
          <XAxis
            type="number"
            tick={TICK}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `${v.toFixed(0)}%`}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={TICK}
            axisLine={false}
            tickLine={false}
            width={170}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0];
              const item = chartData.find(c => c.name === label);
              return (
                <div className="rounded-lg border border-border bg-popover/95 backdrop-blur-sm px-3 py-2 shadow-xl text-xs">
                  <p className="font-semibold text-foreground mb-1">{label}</p>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: d.color as string }} />
                    <span className="text-muted-foreground">View Rate:</span>
                    <span className="font-bold text-foreground">{`${d.value}%`}</span>
                  </div>
                  {item && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="w-2 h-2" />
                      <span className="text-muted-foreground">Views:</span>
                      <span className="font-bold text-foreground">{item.views.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              );
            }}
          />
          <Bar dataKey="value" name="View Rate" radius={[0, 4, 4, 0]} maxBarSize={28}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────
const fmtPct   = (v: number) => v === 0 ? "N/A" : `${v.toFixed(1)}%`;
const fmtCur   = (v: number) => v === 0 ? "N/A" : `$${v.toFixed(2)}`;
const fmtCurK  = (v: number) => {
  if (v === 0) return "N/A";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};
const fmtCount = (v: number) => {
  if (v === 0) return "0";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
  return String(Math.round(v));
};
const fmtPctAxis  = (v: number) => `${v.toFixed(0)}%`;
const fmtCurAxis  = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v.toFixed(0)}`;
const fmtCountAxis= (v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(Math.round(v));

// ─────────────────────────────────────────────
// SHARED: CARD WRAPPER
// ─────────────────────────────────────────────
function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4">
        <p className="text-sm font-bold text-foreground">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────
// SHARED: TOOLTIP
// ─────────────────────────────────────────────
function ChartTooltip({
  active,
  payload,
  label,
  formatter,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  formatter: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover/95 backdrop-blur-sm px-3 py-2 shadow-xl text-xs">
      {label && <p className="font-semibold text-foreground mb-1">{label}</p>}
      {payload.map(p => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-bold text-foreground">{formatter(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// SHARED: SECTION LABEL
// ─────────────────────────────────────────────
function SectionLabel({ n, title }: { n: number; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
        style={{ background: "var(--brand-primary)" }}
      >
        {n}
      </div>
      <span className="text-sm font-bold text-foreground">{title}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

// ─────────────────────────────────────────────
// SECTION 1 — Creative Funnel Aggregate (KPI Strip)
// ─────────────────────────────────────────────
function Section1Kpi({ agg }: { agg: CreativeAggregateTotals }) {
  const volumeKpis = [
    { label: "Impressions",   value: fmtCount(agg.impressions) },
    { label: "Clicks",        value: fmtCount(agg.clicks) },
    { label: "Opt-ins",       value: fmtCount(agg.optins) },
    { label: "Applications",  value: fmtCount(agg.applications) },
    { label: "Closed",        value: fmtCount(agg.closed) },
  ];
  const rateKpis = [
    { label: "CTR",           value: fmtPct(agg.ctr) },
    { label: "Opt-in Rate",   value: fmtPct(agg.optinRate) },
    { label: "App Rate",      value: fmtPct(agg.appRate) },
    { label: "Close Rate",    value: fmtPct(agg.closeRate) },
    { label: "Cost / Optin",  value: fmtCur(agg.costPerOptin) },
    { label: "Cost / App",    value: fmtCur(agg.costPerApp) },
  ];

  return (
    <div className="space-y-3">
      {/* Volume row */}
      <div className="grid grid-cols-5 gap-3">
        {volumeKpis.map((kpi, i) => (
          <div
            key={kpi.label}
            className="rounded-xl border border-border bg-card px-4 py-3 flex flex-col gap-0.5"
          >
            <span className="text-[9px] text-muted-foreground uppercase tracking-widest font-semibold">
              {kpi.label}
            </span>
            <span
              className="text-[18px] font-bold leading-tight"
              style={{ color: METRIC_COLORS[i % METRIC_COLORS.length] }}
            >
              {kpi.value}
            </span>
          </div>
        ))}
      </div>
      {/* Rates row */}
      <div className="grid grid-cols-6 gap-3">
        {rateKpis.map(kpi => (
          <div
            key={kpi.label}
            className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2.5 flex flex-col gap-0.5"
          >
            <span className="text-[9px] text-muted-foreground uppercase tracking-widest font-semibold">
              {kpi.label}
            </span>
            <span className="text-[13px] font-bold text-foreground">{kpi.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SECTION 2 — Creative Funnel Progression
// ─────────────────────────────────────────────
const FUNNEL_STAGES = [
  { key: "impressions"  as const, label: "Impressions" },
  { key: "clicks"       as const, label: "Clicks" },
  { key: "optins"       as const, label: "Opt-ins" },
  { key: "applications" as const, label: "Applications" },
  { key: "closed"       as const, label: "Closed" },
];

const CONV_LABELS: Record<string, string> = {
  "impressions→clicks":       "CTR",
  "clicks→optins":            "Opt-in Rate",
  "optins→applications":      "App Rate",
  "applications→closed":      "Close Rate",
};

function Section2FunnelProgression({ agg }: { agg: CreativeAggregateTotals }) {
  const aggAsRecord: Record<string, number> = {
    impressions:  agg.impressions,
    clicks:       agg.clicks,
    optins:       agg.optins,
    applications: agg.applications,
    closed:       agg.closed,
  };
  const rateAsRecord: Record<string, number> = {
    "impressions→clicks":   agg.ctr,
    "clicks→optins":        agg.optinRate,
    "optins→applications":  agg.appRate,
    "applications→closed":  agg.closeRate,
  };

  const maxVal = agg.impressions || 1;

  return (
    <Card title="Creative Funnel Progression" subtitle="Aggregate across all creative types">
      <div className="space-y-2">
        {FUNNEL_STAGES.map((stage, i) => {
          const val        = aggAsRecord[stage.key];
          const widthPct   = Math.max((val / maxVal) * 100, val > 0 ? 2 : 0);
          const prevKey    = i > 0 ? FUNNEL_STAGES[i - 1].key : null;
          const convKey    = prevKey ? `${prevKey}→${stage.key}` : null;
          const convLabel  = convKey ? CONV_LABELS[convKey] : null;
          const convRate   = convKey ? rateAsRecord[convKey] : null;

          return (
            <React.Fragment key={stage.key}>
              {/* Conversion rate between stages */}
              {convLabel && convRate !== null && (
                <div className="flex items-center gap-2 pl-1 py-0.5">
                  <div className="w-px h-3 bg-border/60 ml-0.5" />
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/40 border border-border/50">
                    <span className="text-[9px] text-muted-foreground font-medium">{convLabel}</span>
                    <span className="text-[10px] font-bold text-[var(--brand-primary)]">
                      {fmtPct(convRate)}
                    </span>
                  </div>
                </div>
              )}

              {/* Bar row */}
              <div className="flex items-center gap-3">
                {/* Label */}
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest w-24 flex-shrink-0 text-right">
                  {stage.label}
                </span>
                {/* Bar */}
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 h-8 bg-muted/20 rounded-md overflow-hidden">
                    <div
                      className="h-full rounded-md transition-all duration-700"
                      style={{
                        width:      `${widthPct}%`,
                        background: `linear-gradient(90deg, var(--brand-gradient-start), var(--brand-gradient-end))`,
                        opacity:    0.9 - i * 0.12,
                      }}
                    />
                  </div>
                  <span className="text-[13px] font-bold text-foreground w-16 flex-shrink-0">
                    {fmtCount(val)}
                  </span>
                  <span className="text-[10px] text-muted-foreground w-10 flex-shrink-0">
                    {agg.impressions > 0
                      ? `${((val / agg.impressions) * 100).toFixed(1)}%`
                      : "—"
                    }
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────
// SECTION 3 — Hook Strength Comparison
// ─────────────────────────────────────────────
type HookMetric = "ctr" | "clicks";

function Section3HookStrength({ data }: { data: CreativePerformanceRow[] }) {
  const [metric, setMetric] = useState<HookMetric>("ctr");

  const chartData = useMemo(() =>
    [...data]
      .filter(r => r.impressions > 0)
      .sort((a, b) => b[metric] - a[metric])
      .map(r => ({ name: r.label, value: r[metric], color: r.color })),
    [data, metric]
  );

  const formatter = metric === "ctr" ? fmtPct : fmtCount;
  const axisFormatter = metric === "ctr" ? fmtPctAxis : fmtCountAxis;

  return (
    <Card title="Hook Strength Comparison" subtitle="Which creatives drive the most top-of-funnel engagement">
      {/* Toggle */}
      <div className="flex gap-1 mb-4">
        {(["ctr", "clicks"] as const).map(m => (
          <button
            key={m}
            onClick={() => setMetric(m)}
            className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-all ${
              metric === m
                ? "bg-[var(--brand-primary)] text-white"
                : "bg-muted/40 text-muted-foreground hover:text-foreground"
            }`}
          >
            {m === "ctr" ? "CTR %" : "Clicks (Volume)"}
          </button>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={HCHART_H}>
        <BarChart layout="vertical" data={chartData} margin={{ top: 0, right: 60, left: 8, bottom: 0 }}>
          <CartesianGrid {...GRID} horizontal={false} />
          <XAxis type="number" tick={TICK} axisLine={false} tickLine={false} tickFormatter={axisFormatter} />
          <YAxis type="category" dataKey="name" tick={TICK} axisLine={false} tickLine={false} width={90} />
          <Tooltip
            content={(props) => (
              <ChartTooltip
                active={props.active}
                payload={props.payload as Array<{ name: string; value: number; color: string }>}
                label={props.label as string}
                formatter={formatter}
              />
            )}
          />
          <Bar dataKey="value" name={metric === "ctr" ? "CTR" : "Clicks"} radius={[0, 4, 4, 0]} maxBarSize={24}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ─────────────────────────────────────────────
// SECTION 4 — Funnel Efficiency by Creative
// (2×2 mini horizontal bar charts — one per cost metric)
// ─────────────────────────────────────────────
interface CostMetricConfig {
  key:    keyof CreativePerformanceRow;
  title:  string;
  color:  string;
}

const COST_METRICS: CostMetricConfig[] = [
  { key: "costPerClick",  title: "Cost per Click",        color: C.chart[0] },
  { key: "costPerOptin",  title: "Cost per Opt-in",       color: C.chart[1] },
  { key: "costPerApp",    title: "Cost per Application",  color: C.chart[3] },
  { key: "costPerClosed", title: "Cost per Closed Deal",  color: C.chart[4] },
];

function CostMiniChart({ config, data }: { config: CostMetricConfig; data: CreativePerformanceRow[] }) {
  const chartData = useMemo(() =>
    [...data]
      .filter(r => r.impressions > 0 && (r[config.key] as number) > 0)
      .sort((a, b) => (a[config.key] as number) - (b[config.key] as number))
      .map(r => ({ name: r.label, value: r[config.key] as number, color: r.color })),
    [data, config.key]
  );

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-semibold text-foreground mb-3">{config.title}</p>
      <ResponsiveContainer width="100%" height={HCHART_H}>
        <BarChart layout="vertical" data={chartData} margin={{ top: 0, right: 50, left: 8, bottom: 0 }}>
          <CartesianGrid {...GRID} horizontal={false} />
          <XAxis type="number" tick={TICK} axisLine={false} tickLine={false} tickFormatter={fmtCurAxis} />
          <YAxis type="category" dataKey="name" tick={TICK} axisLine={false} tickLine={false} width={82} />
          <Tooltip
            content={(props) => (
              <ChartTooltip
                active={props.active}
                payload={props.payload as Array<{ name: string; value: number; color: string }>}
                label={props.label as string}
                formatter={fmtCur}
              />
            )}
          />
          <Bar dataKey="value" name={config.title} radius={[0, 4, 4, 0]} maxBarSize={22}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Section4FunnelEfficiency({ data }: { data: CreativePerformanceRow[] }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        {COST_METRICS.map(cfg => (
          <CostMiniChart key={cfg.key as string} config={cfg} data={data} />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SECTION 5 — Funnel Depth vs Business Outcomes (Bubble Chart)
// ─────────────────────────────────────────────
type XAxisOption = "ctr" | "optinRate" | "appRate" | "closeRate";
type YAxisOption = "optins" | "applications" | "closed";

const X_AXIS_OPTIONS: Array<{ value: XAxisOption; label: string; desc: string }> = [
  { value: "ctr",       label: "CTR",         desc: "Ad Click-Through Rate" },
  { value: "optinRate", label: "Opt-in Rate",  desc: "Clicks → Opt-ins" },
  { value: "appRate",   label: "App Rate",     desc: "Opt-ins → Applications" },
  { value: "closeRate", label: "Close Rate",   desc: "Apps → Closed" },
];

const Y_AXIS_OPTIONS: Array<{ value: YAxisOption; label: string; desc: string }> = [
  { value: "optins",       label: "Opt-ins",       desc: "Total opt-ins" },
  { value: "applications", label: "Applications",  desc: "Total applications" },
  { value: "closed",       label: "Closes",        desc: "Total closed deals" },
];

interface ScatterPoint {
  x:     number;
  y:     number;
  z:     number;
  label: string;
  color: string;
  spend: number;
}

function BubbleLabel(props: { cx?: number; cy?: number; payload?: ScatterPoint }) {
  const { cx = 0, cy = 0, payload } = props;
  if (!payload) return null;
  return (
    <text
      x={cx}
      y={cy + 18}
      textAnchor="middle"
      fill={payload.color}
      fontSize={10}
      fontWeight={600}
    >
      {payload.label}
    </text>
  );
}

function Section5FunnelDepth({ data }: { data: CreativePerformanceRow[] }) {
  const [xKey, setXKey] = useState<XAxisOption>("optinRate");
  const [yKey, setYKey] = useState<YAxisOption>("optins");

  const activeRows = data.filter(r => r.impressions > 0);
  const maxSpend = Math.max(...activeRows.map(r => r.spend), 1);

  const xOpt = X_AXIS_OPTIONS.find(o => o.value === xKey)!;
  const yOpt = Y_AXIS_OPTIONS.find(o => o.value === yKey)!;
  const xFormatter = fmtPctAxis;

  return (
    <Card
      title="Creative Performance Scatter"
      subtitle="Each bubble = one creative type · Bubble size = Spend · Pick axes to compare"
    >
      {/* Axis controls */}
      <div className="flex flex-wrap items-start gap-6 mb-5">
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
            X-axis — <span className="normal-case font-normal">{xOpt.desc}</span>
          </p>
          <div className="flex gap-1 flex-wrap">
            {X_AXIS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setXKey(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                  xKey === opt.value
                    ? "bg-[var(--brand-primary)] text-white shadow-sm"
                    : "bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted/70"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
            Y-axis — <span className="normal-case font-normal">{yOpt.desc}</span>
          </p>
          <div className="flex gap-1 flex-wrap">
            {Y_AXIS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setYKey(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                  yKey === opt.value
                    ? "bg-[var(--brand-primary)] text-white shadow-sm"
                    : "bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted/70"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={CHART_H + 30}>
        <ScatterChart margin={{ top: 10, right: 30, bottom: 30, left: 10 }}>
          <CartesianGrid {...GRID} />
          <XAxis
            type="number"
            dataKey="x"
            name={xOpt.label}
            tick={TICK}
            axisLine={AXIS}
            tickLine={false}
            tickFormatter={xFormatter}
            label={{ value: xOpt.label, position: "insideBottom", offset: -15, style: { fontSize: 11, fill: "var(--muted-foreground)", fontWeight: 600 } }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name={yOpt.label}
            tick={TICK}
            axisLine={false}
            tickLine={false}
            tickFormatter={fmtCountAxis}
            width={44}
            label={{ value: yOpt.label, angle: -90, position: "insideLeft", offset: 10, style: { fontSize: 11, fill: "var(--muted-foreground)", fontWeight: 600 } }}
          />
          <ZAxis type="number" dataKey="z" range={[100, 700]} name="Spend" />
          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const pt = payload[0]?.payload as ScatterPoint;
              return (
                <div className="rounded-lg border border-border bg-popover/95 backdrop-blur-sm px-3 py-2 shadow-xl text-xs space-y-1">
                  <p className="font-bold text-foreground flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: pt.color }} />
                    {pt.label}
                  </p>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">{xOpt.label}</span>
                    <span className="font-semibold">{fmtPctAxis(pt.x)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">{yOpt.label}</span>
                    <span className="font-semibold">{fmtCountAxis(pt.y)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Spend</span>
                    <span className="font-semibold">${pt.spend.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                  </div>
                </div>
              );
            }}
          />
          {activeRows.map(row => (
            <Scatter
              key={row.type}
              name={row.label}
              data={[{
                x:     row[xKey] as number,
                y:     row[yKey] as number,
                z:     Math.max((row.spend / maxSpend) * 100, 10),
                label: row.label,
                color: row.color,
                spend: row.spend,
              }]}
              fill={row.color}
              opacity={0.9}
              shape={(shapeProps: { cx?: number; cy?: number; payload?: ScatterPoint }) => {
                const { cx = 0, cy = 0, payload } = shapeProps;
                if (!payload) return <circle cx={cx} cy={cy} r={0} />;
                const r = Math.sqrt((Math.max((payload as ScatterPoint & { z: number }).z ?? 10, 10) / 100) * maxSpend / maxSpend * 2800 + 400);
                return (
                  <g>
                    <circle cx={cx} cy={cy} r={r} fill={payload.color} fillOpacity={0.25} stroke={payload.color} strokeWidth={2} />
                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill={payload.color} fontSize={11} fontWeight={700}>
                      {payload.label}
                    </text>
                  </g>
                );
              }}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>

      <p className="text-[10px] text-muted-foreground/60 text-right mt-1">Bubble size = Ad Spend</p>
    </Card>
  );
}

// ─────────────────────────────────────────────
// SECTION 6 — Drop-off Analysis by Creative
// ─────────────────────────────────────────────
interface DropOffChartData {
  name:            string;
  color:           string;
  clicksToOptin:   number;
  optinToApp:      number;
  appToClose:      number;
}

function Section6DropOff({ data }: { data: CreativePerformanceRow[] }) {
  const chartData: DropOffChartData[] = data
    .filter(r => r.impressions > 0)
    .map(r => ({
      name:           r.label,
      color:          r.color,
      clicksToOptin:  parseFloat(r.dropClicksToOptin.toFixed(1)),
      optinToApp:     parseFloat(r.dropOptinToApp.toFixed(1)),
      appToClose:     parseFloat(r.dropAppToClose.toFixed(1)),
    }));

  const dropFormatter = (v: number) => `${v.toFixed(1)}%`;

  return (
    <Card
      title="Drop-off Analysis by Creative"
      subtitle="% of users lost at each funnel transition — lower is better"
    >
      <div className="flex flex-wrap gap-x-5 gap-y-1 mb-4">
        {[
          { label: "Clicks → Opt-ins",       color: METRIC_COLORS[0] },
          { label: "Opt-ins → Applications", color: METRIC_COLORS[1] },
          { label: "Applications → Closed",  color: METRIC_COLORS[2] },
        ].map(it => (
          <span key={it.label} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <span className="w-3 h-[2px] rounded-full" style={{ background: it.color }} />
            {it.label}
          </span>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={180}>
        <BarChart layout="vertical" data={chartData} margin={{ top: 0, right: 50, left: 8, bottom: 0 }}>
          <CartesianGrid {...GRID} horizontal={false} />
          <XAxis
            type="number"
            tick={TICK}
            axisLine={false}
            tickLine={false}
            tickFormatter={fmtPctAxis}
            domain={[0, 100]}
          />
          <YAxis type="category" dataKey="name" tick={TICK} axisLine={false} tickLine={false} width={90} />
          <Tooltip
            content={(props) => (
              <ChartTooltip
                active={props.active}
                payload={props.payload as Array<{ name: string; value: number; color: string }>}
                label={props.label as string}
                formatter={dropFormatter}
              />
            )}
          />
          <Bar dataKey="clicksToOptin"  name="Clicks → Opt-ins"       fill={METRIC_COLORS[0]} radius={[0, 3, 3, 0]} maxBarSize={14} />
          <Bar dataKey="optinToApp"     name="Opt-ins → Applications" fill={METRIC_COLORS[1]} radius={[0, 3, 3, 0]} maxBarSize={14} />
          <Bar dataKey="appToClose"     name="Applications → Closed"  fill={METRIC_COLORS[2]} radius={[0, 3, 3, 0]} maxBarSize={14} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ─────────────────────────────────────────────
// SECTION 7 — Creative Ranking
// ─────────────────────────────────────────────
function ScoreBar({ score }: { score: number }) {
  const color =
    score >= 70 ? "bg-green-500"  :
    score >= 45 ? "bg-yellow-500" :
    "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-muted/40 overflow-hidden max-w-[80px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-[12px] font-bold tabular-nums ${
        score >= 70 ? "text-green-400" : score >= 45 ? "text-yellow-400" : "text-red-400"
      }`}>
        {score}
      </span>
    </div>
  );
}

function Section7Ranking({ data }: { data: CreativePerformanceRow[] }) {
  const ranked = [...data]
    .filter(r => r.impressions > 0)
    .sort((a, b) => a.rank - b.rank);

  const cols = ["#", "Creative", "CTR", "App Rate", "Close Rate", "Cost/App", "Score"];

  return (
    <Card title="Creative Ranking" subtitle="Scored on CTR (25%) · App Rate (30%) · Close Rate (25%) · Cost/App (20%)">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {cols.map(c => (
                <th
                  key={c}
                  className="px-3 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap first:pl-2"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ranked.map((row, i) => (
              <tr key={row.type} className={i < ranked.length - 1 ? "border-b border-border/40" : ""}>
                <td className="px-3 py-3 pl-2">
                  <span className={`text-[13px] font-bold ${
                    row.rank === 1 ? "text-yellow-400" :
                    row.rank === 2 ? "text-muted-foreground" :
                    "text-muted-foreground/60"
                  }`}>
                    #{row.rank}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: row.color }} />
                    <span className="text-[12px] font-semibold text-foreground">{row.label}</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-[12px] text-foreground">{fmtPct(row.ctr)}</td>
                <td className="px-3 py-3 text-[12px] text-foreground">{fmtPct(row.appRate)}</td>
                <td className="px-3 py-3 text-[12px] text-foreground">{fmtPct(row.closeRate)}</td>
                <td className="px-3 py-3">
                  <span className="text-[12px] font-bold" style={{ color: row.color }}>
                    {fmtCur(row.costPerApp)}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <ScoreBar score={row.score} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────
interface CreativePerformanceSectionProps {
  data:    CreativePerformanceRow[];
  rows:    MetaLeadGenRow[];
  filters: MetaLeadGenFilters;
}

export function CreativePerformanceSection({ data, rows, filters }: CreativePerformanceSectionProps) {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-1">
        <h3 className="text-base font-bold text-foreground">Creative Performance Analysis</h3>
        <p className="text-sm text-muted-foreground">
          Full-funnel performance breakdown across creative types — from attention to close.
        </p>
      </div>

      {/* 0 — Individual Creative Cards */}
      <div className="space-y-3">
        <SectionLabel n={0} title="Individual Creative Performance" />
        <Section0IndividualCards rows={rows} filters={filters} />
      </div>

      {/* Hook Strength */}
      <div className="space-y-3">
        <SectionLabel n={3} title="Hook Strength Comparison" />
        <Section3HookStrength data={data} />
      </div>

      {/* 5 — Scatter */}
      <div className="space-y-3">
        <SectionLabel n={5} title="Funnel Depth vs Business Outcomes" />
        <Section5FunnelDepth data={data} />
      </div>

      {/* 6 — Drop-off */}
      <div className="space-y-3">
        <SectionLabel n={6} title="Drop-off Analysis by Creative" />
        <Section6DropOff data={data} />
      </div>

      {/* 7 — Ranking */}
      <div className="space-y-3">
        <SectionLabel n={7} title="Creative Ranking" />
        <Section7Ranking data={data} />
      </div>
    </div>
  );
}
